// src/js/core/router.js
//
// ハイブリッドレンダリング方式のクライアントルーター（00_情報設計_全体アーキテクチャ.md 第3章）。
//
// 原則:
//   - 初回アクセスは常にサーバーHTMLをそのまま使う。このモジュールは起動時にDOMを
//     再描画しない（00文書 原則2「SPAシェル禁止」）。init() は onNavigateエラー時の
//     フォールバックの担保と、以後のリンククリック捕捉の準備だけを行う。
//   - クライアント遷移はPJAX方式: 内部リンクのクリックを捕捉し、遷移先URLの
//     完全なサーバーHTMLを fetch して `#xte-app` の中身を丸ごと差し替え、
//     そのうえでルートモジュールの mount() が水和（漸進的強化）する。
//     つまりクライアント遷移後のDOMは「そのURLへ直接アクセスした場合」と同一になり、
//     水和経路も初回ロード（main.js の hydrateCurrentRoute）と完全に一致する。
//     JSが本文を組み立てることはしない（00文書 原則2「サーバー完結の原則」）。
//
//     旧実装はルートごとにRESTからデータを取得して mount() に渡すだけで、
//     SSR専用ルート（downloads / search / playlists / notifications 等）の
//     mount() は水和のみ・本文の差し替え処理が存在しなかった。その結果
//     「URL・タイトル・ナビのアクティブ表示だけが変わり、本文は前のページのまま」
//     という壊れ方が全SSR専用ルートで起きていた（本方式への変更の動機）。
//   - 未対応のルート・取得失敗時は通常のブラウザ遷移にフォールバックする
//     （実装漏れがあってもサーバー側テンプレートが必ず正しいHTMLを返せるため機能は壊れない）。
//
// 1回の遷移で必ず揃えるもの（欠けると「押しても何も起きない」ように見える）:
//   1. DOM（mount コールバック）
//   2. URL（history.pushState / popstate 由来なら replaceState で state を補完）
//   3. document.title
//   4. フォーカス移動とライブリージョンでの読み上げ
//   5. `xte-router:navigated` の発火（ボトムナビのアクティブ表示はこれで追従する）
// 上記のどれかで例外が出た場合は、握りつぶさず通常のブラウザ遷移へ落とす。
//
// 文言ストアの補完（refreshCopyStore）を使う理由は primeFullCopyStore() のコメント参照。
import { refreshCopyStore } from './copy.js';

// ルート単位のコード分割（43_パフォーマンス_CoreWebVitals.md 7.1節）。
// 各エントリモジュールは他担当が実装する（routes/*-entry.js）。ここではマッピングと
// 動的importの実行のみを担う。エントリが未実装のルートへ遷移した場合は
// loadRouteModule() が例外化し、通常のブラウザ遷移へフォールバックする（下記 navigate() 参照）。
const ROUTE_LOADERS = {
  feed: () => import('../routes/feed-entry.js'),
  video: () => import('../routes/video-entry.js'),
  grid: () => import('../routes/grid-entry.js'),
  playlists: () => import('../routes/playlists-entry.js'),
  playlist: () => import('../routes/playlist-entry.js'),
  search: () => import('../routes/search-entry.js'),
  notifications: () => import('../routes/notifications-entry.js'),
  downloads: () => import('../routes/downloads-entry.js'),
  settings: () => import('../routes/settings-entry.js'),
  submit: () => import('../routes/submit-entry.js'),
  'tags-ranking': () => import('../routes/tags-ranking-entry.js'),
  'categories-index': () => import('../routes/categories-index-entry.js'),
  'performers-index': () => import('../routes/performers-index-entry.js'),
  'contributors-ranking': () => import('../routes/contributors-ranking-entry.js'),
};

/**
 * ルート定義。上から順に評価し、最初にマッチしたものを採用する。
 *
 * PJAX方式（本文はサーバーHTMLの差し替えで更新する）のため、ここでの照合結果は
 * 「どのエントリモジュールで水和するか」の決定にだけ使う。旧実装が持っていた
 * apiRequest()（RESTリクエストの組み立て）は、水和用データを遷移先HTMLの
 * `#xte-initial-data`（初回SSRロードと同じ供給経路）から得るようにしたため撤去した。
 */
const ROUTE_DEFS = [
  { name: 'feed', test: (pathname) => pathname === '/' },
  { name: 'video', test: (pathname) => pathname.match(/^\/video\/(?:([^/]+)-)?(\d+)\/$/) },
  { name: 'playlist', test: (pathname) => pathname.match(/^\/playlist\/(?:([^/]+)-)?(\d+)\/$/) },
  { name: 'playlists', test: (pathname) => pathname.match(/^\/playlists\/(?:page\/(\d+)\/)?$/) },
  { name: 'grid', test: (pathname) => pathname.match(/^\/videos\/(?:page\/(\d+)\/)?$/) },
  { name: 'grid', test: (pathname) => pathname.match(/^\/tag\/([^/]+)\/(?:page\/(\d+)\/)?$/) },
  { name: 'grid', test: (pathname) => pathname.match(/^\/category\/([^/]+)\/([^/]+)\/(?:page\/(\d+)\/)?$/) },
  { name: 'grid', test: (pathname) => pathname.match(/^\/performer\/([^/]+)\/(?:page\/(\d+)\/)?$/) },
  { name: 'grid', test: (pathname) => pathname.match(/^\/quality\/([^/]+)\/(?:page\/(\d+)\/)?$/) },
  { name: 'grid', test: (pathname) => pathname.match(/^\/duration\/([^/]+)\/(?:page\/(\d+)\/)?$/) },
  { name: 'search', test: (pathname) => pathname === '/search/' },
  { name: 'notifications', test: (pathname) => pathname === '/notifications/' },
  { name: 'downloads', test: (pathname) => pathname === '/downloads/' },
  { name: 'settings', test: (pathname) => pathname === '/settings/' },
  { name: 'submit', test: (pathname) => pathname === '/submit/' },
  { name: 'tags-ranking', test: (pathname) => pathname === '/tags/' },
  { name: 'categories-index', test: (pathname) => pathname === '/categories/' },
  { name: 'performers-index', test: (pathname) => pathname === '/performers/' },
  { name: 'contributors-ranking', test: (pathname) => pathname === '/contributors/' },
];

/**
 * URLをルート定義に照合する。マッチしなければ null（＝通常のブラウザ遷移に委ねるべき）。
 * @param {URL} url
 */
export function matchRoute(url) {
  for (const def of ROUTE_DEFS) {
    const match = def.test(url.pathname);
    if (match) {
      return { name: def.name, match, def, url };
    }
  }
  return null;
}

/**
 * ルート名から対応するエントリモジュールを動的importする。main.js が初回ロード時の
 * 「サーバーHTMLへの漸進的強化（00文書 原則3）」で使うのと、router.js内部の
 * クライアント遷移が同じマッピングを共有するために公開する。
 * @param {string} routeName
 */
export function loadRouteModule(routeName) {
  const loader = ROUTE_LOADERS[routeName];
  if (!loader) return Promise.reject(new Error(`[router] 未登録のルートです: ${routeName}`));
  return loader();
}

const INTERNAL_LINK_SELECTOR = 'a[href]';

/**
 * クライアント遷移の完了を知らせるカスタムイベント名。
 * `components/xte-bottom-nav.js` がアクティブタブの同期に使う（同ファイルは
 * 以前 window.history を monkey-patch してURL変化を検知していたが、本イベントの
 * 導入によりそのパッチは不要になった）。
 */
const NAVIGATED_EVENT = 'xte-router:navigated';

/**
 * ルーター自身が積んだヒストリーエントリであることを示す印。
 *
 * `xte-search-modal.js`（`{xteSearchModal}`）・`xte-comments.js`（`{xteCommentsOpen}`）・
 * `xte-grid.js`（`{xteGridFilter}`）・`routes/search-entry.js`（`{xteRoute:'search'}`）も
 * それぞれ独自の state を pushState する。旧実装のように `state.xteRoute` の有無だけで
 * 判定すると他担当のエントリを自分のものと取り違えるため、専用のキーで区別する。
 */
const HISTORY_MARKER = 'xteRouter';

/** 遷移をスクリーンリーダーへ知らせるライブリージョンのid。 */
const ANNOUNCER_ID = 'xte-route-announcer';

/** 遷移後にフォーカスを移す先。スキップリンク（`#xte-app`）の飛び先と同一。 */
const MAIN_CONTAINER_ID = 'xte-app';

/**
 * サーバーHTMLが埋め込む2つのJSONブロックのid（parts/app-shell-close.php が出力。
 * 実際のIDと構造は同ファイルで確認済み: どちらも `<script type="application/json">`）。
 *   - xte-initial-data: 現在ページのAPI相当データ。未設定ページでは `null`。
 *     main.js の readEmbeddedInitialData() と、本ルーターの水和データ供給が読む。
 *   - xte-copy-data: 現在ページで使う文言の平坦な { key => text }。core/copy.js が読む。
 * PJAX遷移では両ブロックを遷移先HTMLのもので差し替える。古いページのデータの
 * まま水和すると、別ルートのデータ・文言で画面を組んでしまい壊れるため。
 */
const INITIAL_DATA_ID = 'xte-initial-data';
const COPY_DATA_ID = 'xte-copy-data';

/* ------------------------------------------------------------------ *
 * スクロール位置の記録・リセット・復元
 *
 * このテーマでは html / body はスクロールしない。layout.css の `.xte-shell` が
 * `overflow: hidden` を持ち、実際のスクロールポートは `#xte-app`
 * （`main.xte-shell__main`、`overflow-y: auto`）である。旧実装は
 * window.scrollY / window.scrollTo を使っていたため、記録は常に 0・
 * 先頭へのリセットは空振りになっていた。PJAXは `#xte-app` の中身だけを
 * replaceChildren で差し替えるため要素自身の scrollTop は差し替え後も残り、
 * 「関連動画へ遷移しても前ページのスクロール位置のまま表示される」
 * 不具合の直接原因だった。
 *
 * ネストしたスクローラについて: フィードの `.xte-feed`（position: fixed の
 * 独立スクローラ）とグリッドの `.xte-grid__viewport` は各コンポーネントが
 * 自前のスクロール管理を持ち、PJAX差し替えで毎回新しい要素になるため
 * scrollTop は必ず 0 から始まる。ルーターが個別に触る必要はないが、
 * マーカー付きスクローラ（data-xte-scroll-root。xte-bottom-nav.js が
 * 同一タブ再タップの scrollTo 先として使う規約）だけは、水和処理が
 * スクロールを動かした場合の保険としてリセット対象に含める。
 * ------------------------------------------------------------------ */
function getScrollContainer() {
  return document.getElementById(MAIN_CONTAINER_ID);
}

function readScrollTop() {
  const el = getScrollContainer();
  return el ? el.scrollTop : window.scrollY;
}

/**
 * スクロール位置を設定する。View Transition の更新コールバック内
 * （= 差し替え直後・描画前）で呼ばれるため、変更はトランジションの
 * スナップショット提示前に反映され、「一瞬前の位置が見えてから飛ぶ」
 * ちらつきは起きない。
 * @param {number} y
 */
function setScrollTop(y) {
  const el = getScrollContainer();
  if (el) {
    el.scrollTop = y;
    el.scrollLeft = 0;
  }
  // 保険: コンテナが無い異常時はwindowへ、ある場合もフォーカス移動等で
  // window側がスクロールしていた可能性に備えて先頭へ戻す。
  window.scrollTo(0, el ? 0 : y);
}

/** 新規遷移用: 本文コンテナとページ内のマーカー付きスクローラを先頭へ戻す。 */
function resetScrollToTop() {
  setScrollTop(0);
  const el = getScrollContainer();
  if (!el) return;
  for (const scroller of el.querySelectorAll('[data-xte-scroll-root]')) {
    scroller.scrollTop = 0;
    scroller.scrollLeft = 0;
  }
}

/* ------------------------------------------------------------------ *
 * PJAX: サーバーHTMLの取得と `#xte-app` の差し替え
 * ------------------------------------------------------------------ */

/**
 * 遷移先URLの完全なサーバーHTMLを取得して解析する。
 * 想定した構造でない場合（HTTPエラー・HTML以外・`#xte-app` 不在・別URLへの
 * リダイレクト）はすべて例外にする。呼び出し側（navigate）が捕捉して
 * 通常のフルページ遷移へフォールバックするため、ここでは握りつぶさない。
 *
 * @param {URL} url
 * @returns {Promise<{doc: Document, appRoot: HTMLElement, title: string}>}
 */
async function fetchServerPage(url) {
  const res = await fetch(url.href, {
    credentials: 'same-origin',
    headers: { Accept: 'text/html' },
  });

  if (!res.ok) {
    throw new Error(`[router] 遷移先HTMLの取得に失敗しました: HTTP ${res.status} ${url.href}`);
  }

  // 別URLへリダイレクトされた場合、履歴に積むURLと実際の内容が食い違う。
  // まれなケース（恒久リダイレクト設定・認証誘導等）なので、正確さを優先して
  // フルページ遷移に委ねる（フォールバック側で同じリダイレクトが再度正しく働く）。
  const finalUrl = new URL(res.url || url.href, window.location.href);
  if (res.redirected && finalUrl.href !== url.href) {
    throw new Error(`[router] 遷移先が別URLへリダイレクトされました: ${url.href} -> ${finalUrl.href}`);
  }

  const contentType = res.headers.get('content-type') || '';
  if (!/text\/html/i.test(contentType)) {
    throw new Error(`[router] 遷移先がHTMLではありません: ${contentType} ${url.href}`);
  }

  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const appRoot = doc.getElementById(MAIN_CONTAINER_ID);
  if (!appRoot) {
    throw new Error(`[router] 遷移先HTMLに #${MAIN_CONTAINER_ID} が含まれていません: ${url.href}`);
  }

  return { doc, appRoot, title: doc.title || '' };
}

/**
 * 取得済みドキュメントの `#xte-initial-data` を読む（main.js の
 * readEmbeddedInitialData() と同じ寛容さ: 不在・壊れたJSONは null 扱い）。
 * @param {Document} doc
 */
function readInitialDataFrom(doc) {
  const el = doc.getElementById(INITIAL_DATA_ID);
  if (!el) return null;
  try {
    return JSON.parse(el.textContent ?? 'null');
  } catch {
    return null;
  }
}

/**
 * 取得したサーバーHTMLを現在のドキュメントへ反映する（PJAXの中核）。
 * `#xte-app` の中身の丸ごと差し替えと、埋め込みJSONブロック2つの差し替えを行う。
 * 現在のドキュメントに `#xte-app` が無い場合は例外（呼び出し側でフルページ遷移へ）。
 *
 * @param {{doc: Document, appRoot: HTMLElement}} page fetchServerPage() の戻り値
 * @returns {HTMLElement} 差し替え後のコンテナ（現ドキュメント側の `#xte-app`）
 */
function applyServerPage(page) {
  const container = document.getElementById(MAIN_CONTAINER_ID);
  if (!container) {
    throw new Error(`[router] 現在のドキュメントに #${MAIN_CONTAINER_ID} がありません。`);
  }

  // <main id="xte-app"> 自身の属性も遷移先HTMLへ揃える（現状は class 固定だが、
  // テンプレート側が将来ルート別の属性を付けても本文と一緒に追従させるため）。
  syncContainerAttributes(container, page.appRoot);

  // DOMParser製ノードは所有ドキュメントが異なるため adoptNode で引き取ってから挿入する。
  // SSRされた <script> はこの時点で「実行済み」フラグ相当の扱いになり実行されないが、
  // 本文内にスクリプトを置かないのがこのテーマの設計（データはJSONブロック経由）。
  const fragment = document.createDocumentFragment();
  for (const node of Array.from(page.appRoot.childNodes)) {
    fragment.appendChild(document.adoptNode(node));
  }
  container.replaceChildren(fragment);

  // 埋め込みJSONブロックの差し替え。initial-data は遷移先に無ければ 'null' へ
  // 明示的に戻す（前ページのデータが残ると次の読み取りが誤る）。copy-data は
  // 文言辞書という加算的な性質のため、遷移先に無い場合は既存を残す。
  replaceEmbeddedJson(INITIAL_DATA_ID, page.doc, 'null');
  replaceEmbeddedJson(COPY_DATA_ID, page.doc, null);

  return container;
}

/**
 * コンテナ要素の属性を遷移先HTML側の同要素へ揃える。
 * `tabindex` だけは同期対象から外す: focusMainContainer() がフォーカス移動のために
 * 実行時に付与する属性であり、サーバーHTML側には存在しないため。
 *
 * @param {HTMLElement} container 現ドキュメントの `#xte-app`
 * @param {HTMLElement} fetchedMain 遷移先ドキュメントの `#xte-app`
 */
function syncContainerAttributes(container, fetchedMain) {
  const preserved = new Set(['tabindex']);
  for (const attr of Array.from(container.attributes)) {
    if (preserved.has(attr.name)) continue;
    if (!fetchedMain.hasAttribute(attr.name)) container.removeAttribute(attr.name);
  }
  for (const attr of Array.from(fetchedMain.attributes)) {
    if (preserved.has(attr.name)) continue;
    container.setAttribute(attr.name, attr.value);
  }
}

/**
 * 埋め込みJSONブロック（`<script type="application/json">`）を遷移先HTMLの内容で
 * 差し替える。要素ごと入れ替えず textContent の書き換えに留めるのは、既存要素への
 * 参照（デバッグツール等）を無効化しないためと、位置（wp_footer 直前）を保つため。
 *
 * @param {string} id ブロックのid
 * @param {Document} doc 遷移先ドキュメント
 * @param {string|null} emptyFallback 遷移先にブロックが無い場合に書き込む内容。
 *   null なら既存の内容を残す。
 */
function replaceEmbeddedJson(id, doc, emptyFallback) {
  const fresh = doc.getElementById(id);
  const live = document.getElementById(id);

  if (fresh) {
    if (live) {
      live.textContent = fresh.textContent ?? '';
    } else {
      // 現ドキュメント側にブロックが無い（想定外だが、テンプレート差異に耐える）。
      // application/json の script は挿入しても実行されないため安全に移せる。
      document.body.appendChild(document.adoptNode(fresh));
    }
    return;
  }

  if (live && emptyFallback !== null) {
    live.textContent = emptyFallback;
  }
}

/* ------------------------------------------------------------------ *
 * 文言ストアの補完
 *
 * core/copy.js はモジュール読込時に一度だけ `#xte-copy-data` を読んでストアを
 * 構築する（本ファイルからは書き換え不可の内部状態）。PJAXでブロックのDOMを
 * 差し替えてもストアには反映されないため、遷移先ページ固有の文言キーが
 * 不足しうる。copy.js には「不足キー検知 → セット全体を自動再取得」の救済が
 * あるが、非同期のため mount() 中の copy() 呼び出しには間に合わず、
 * `[[キー名]]` がそのまま画面に残る。
 *
 * そこで最初のクライアント遷移の完了前に一度だけ GET /xte/v1/copy で
 * 文言セット全体を取得しておく（refreshCopyStore は全キーを返すエンドポイントを
 * 叩くため、以後の遷移では不足が発生しない）。毎遷移のリクエストにはしない。
 * refreshCopyStore は失敗を内部で握って解決するため、ここが遷移を失敗させる
 * ことはない（失敗時は copy.js 側の不足キー再取得が引き続き安全網になる）。
 * ------------------------------------------------------------------ */
let copyStorePrimed = null;

function primeFullCopyStore() {
  copyStorePrimed ??= refreshCopyStore().catch(() => {});
  return copyStorePrimed;
}

/**
 * 遷移先タイトルの手掛かりを、クリックされたリンク自身から拾う。
 * ここで返るのはいずれも「サーバーがレンダリングした文字列」であり、
 * JS側でタイトル文言を組み立てているわけではない（SSR優先の原則を壊さないため）。
 *
 * `textContent` は使わない。グリッドのセルのように再生時間・いいね数まで
 * 含んでしまい、タイトルとして意味を成さない文字列になるため。
 *
 * @param {HTMLAnchorElement} link
 * @returns {string|null}
 */
function titleHintFromLink(link) {
  return link.dataset.xteTitle || link.getAttribute('title') || link.getAttribute('aria-label') || null;
}

/** クリックされたリンクを横取りしてよいかを判定する（00文書 3.1節の横取り条件）。 */
function shouldInterceptClick(event, link) {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false; // 左クリック以外（中クリックの新規タブ等）は素通り
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (link.hasAttribute('data-xte-no-route')) return false;
  if (link.hasAttribute('download')) return false;
  if (link.target && link.target !== '_self') return false;
  if (link.hasAttribute('rel') && link.getAttribute('rel').split(/\s+/).includes('external')) return false;

  const href = link.getAttribute('href') || '';
  if (!href || href.startsWith('#')) return false;
  if (/^(mailto|tel|javascript|blob|data):/i.test(href)) return false;

  let url;
  try {
    url = new URL(link.href, window.location.href);
  } catch {
    return false;
  }
  if (url.origin !== window.location.origin) return false;
  if (!/^https?:$/.test(url.protocol)) return false;

  return url;
}

/**
 * ルーターを起動する。1ページに1回だけ呼ぶ（main.js から）。
 *
 * @param {object} options
 * @param {(routeName: string, mod: any, data: any, url: URL) => void|Promise<void>} options.mount
 *   ルートモジュールを実際にDOMへ描画するコールバック（main.js側が各コンポーネントの
 *   マウント方法を知っているため、router.js自体は描画方法に関知しない）。
 * @param {(error: Error, url: URL) => void} [options.onError] 遷移失敗時の通知フック（RUM送出用）
 * @returns {{ navigate(url: URL, opts?: {push?: boolean}): Promise<void>, dispose(): void }}
 */
export function initRouter({ mount, onError } = {}) {
  if (typeof mount !== 'function') {
    throw new Error('[router] initRouter({ mount }) には描画コールバックが必須です。');
  }

  // 00文書 3.2節: スクロール位置はメモリのみのMapで保持し、リロードでは復元しない
  // （ブラウザのネイティブ復元と競合させないため scrollRestoration を明示的に手動化する）。
  const scrollPositions = new Map(); // url(string) -> 実スクロールコンテナ（#xte-app）の scrollTop
  const controller = new AbortController();

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  let currentUrlKey = window.location.href;
  // 直近の遷移要求だけを採用するための世代番号。旧実装は navigating フラグを立てるだけで
  // if文の中身が空になっており、連続クリック時の競合を実際には何も防いでいなかった。
  let navSeq = 0;

  /* ---------- <title> の解決（フォールバック側） ---------------------- *
   * PJAX化により、通常経路のタイトルは「取得したサーバーHTMLの <title>」
   * （= PHP側 `XTE_Seo::build_document_title()`、40_SEO戦略 4.3節の出力そのもの）を
   * そのまま使う。本ヘルパ resolveTitle() が使われるのは、取得したHTMLの
   * <title> が空という異常時だけになった。
   *
   * その場合も「サーバーが生成した文字列だけを使う」制約を守れる範囲で、
   * 次の優先順位で解決する。40文書のタイトル生成規則をJS側に再実装することはしない。
   *   1. 埋め込み初期データの `meta.document_title`（サーバー生成値）
   *   2. 同一セッション中にサーバーから実際に受け取った、そのURLのタイトル
   *      （URL単位で記憶しておき、戻る/進むではこれに復帰できる）
   *   3. クリックされたリンクがサーバーHTML上に持っていた文言（data-xte-title /
   *      title / aria-label）＋ SSRの `<title>` から実測したサイト名サフィックス
   * いずれも得られない場合はタイトルを書き換えない（誤った値を出すより据え置く）。
   * ------------------------------------------------------------------ */
  const titleByUrl = new Map([[window.location.href, document.title]]);
  // `{core} | {サイト名}`（40文書 4.3節）のサフィックス部分。サイト名をJSに
  // ハードコードしないよう、サーバーが返した <title> から実測で切り出す。
  const siteTitleSuffix = (() => {
    const idx = document.title.lastIndexOf(' | ');
    return idx === -1 ? '' : document.title.slice(idx);
  })();

  function resolveTitle(url, data, titleHint) {
    const fromServer = data && data.meta ? data.meta.document_title : null;
    if (typeof fromServer === 'string' && fromServer !== '') return fromServer;

    const remembered = titleByUrl.get(url.href);
    if (remembered) return remembered;

    const hint = typeof titleHint === 'string' ? titleHint.trim() : '';
    if (hint === '') return null;
    return hint + siteTitleSuffix;
  }

  /* ---------- アクセシビリティ（遷移の通知とフォーカス移動） ----------- *
   * 設計書にはSPA遷移時のフォーカス管理・ライブリージョンの規定が無い
   * （_共通規約 14章の「動的更新は aria-live で通知」のみ）。
   * ここでは `#xte-app` の中身が丸ごと差し替わる以上、
   *   - フォーカスが破棄されたノードごと <body> に落ちてしまう
   *   - スクリーンリーダー利用者に画面が変わったことが伝わらない
   * の2点を最低限解消する。
   * ------------------------------------------------------------------ */
  let announcer = null;

  function getAnnouncer() {
    if (announcer && announcer.isConnected) return announcer;
    announcer = document.getElementById(ANNOUNCER_ID);
    if (!announcer) {
      // サーバーHTMLを作り替えるのではなく補助要素を1つ追加するだけなので、
      // 原則2（サーバー完結）には抵触しない。
      announcer = document.createElement('div');
      announcer.id = ANNOUNCER_ID;
      announcer.className = 'xte-visually-hidden'; // 既存の視覚的非表示ユーティリティ
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      document.body.appendChild(announcer);
    }
    return announcer;
  }

  function announceNavigation(title) {
    const text = (title ?? '').trim();
    if (text === '') return;
    getAnnouncer().textContent = text;
  }

  function focusMainContainer() {
    const main = document.getElementById(MAIN_CONTAINER_ID);
    if (!main) return;
    // <main> は既定でフォーカス不能。スキップリンク（`#xte-app`）と同じ飛び先へ
    // プログラム的にフォーカスを移し、読み上げ位置を新しい本文の先頭に戻す。
    // ポインタ操作由来のプログラム的フォーカスは :focus-visible に該当しないため、
    // マウス利用者にフォーカスリングは出ない（7.4節の要件を壊さない）。
    if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
    main.focus({ preventScroll: true });
  }

  /**
   * @param {URL} url
   * @param {object} [opts]
   * @param {boolean} [opts.push] true でヒストリーに新規追加、false は popstate 由来
   * @param {string|null} [opts.titleHint] 遷移先タイトルの手掛かり（サーバー生成文字列）
   */
  async function navigate(url, { push = true, titleHint = null } = {}) {
    const route = matchRoute(url);
    if (!route) {
      window.location.assign(url.href); // 未対応ルートは通常遷移にフォールバック（00文書 3.1節）
      return;
    }

    // 離脱前のスクロール位置を記録する。window.scrollY は常に 0 になるため
    // 使わない（実スクロールは #xte-app で起きる。getScrollContainer() のコメント参照）。
    scrollPositions.set(currentUrlKey, readScrollTop());

    const seq = ++navSeq;
    try {
      // エントリモジュール取得・サーバーHTML取得・文言ストア補完（初回のみ実通信）を
      // 並列に走らせる。navSeq（世代番号）により、連打・二重遷移では常に最後の要求
      // だけが勝つ: 古い世代は fetch 完了直後と描画直前の2箇所のチェックで捨てられ、
      // DOMには一切触れない。
      const [mod, page] = await Promise.all([
        loadRouteModule(route.name),
        fetchServerPage(url),
        primeFullCopyStore(),
      ]);
      if (seq !== navSeq) return; // より新しい遷移要求が始まっているので、この結果は捨てる

      const applyRender = async () => {
        // startViewTransition のコールバックは次フレームで実行されるため、その間に
        // 後発の遷移が始まっていたらここでも中止する（2つの遷移がDOMを触り合わない）。
        if (seq !== navSeq) return;

        // PJAX本体: サーバーHTMLで `#xte-app` の中身と埋め込みJSONブロックを
        // 丸ごと差し替える。これで本文は「遷移先URLへ直接アクセスした場合」と
        // 同一になり、以後の水和は初回ロードと同じ前提で動く。
        applyServerPage(page);

        // 新規遷移は水和より前の時点で先頭へ戻しておく。差し替え後も #xte-app の
        // scrollTop は前ページの位置のまま残るため、水和処理が可視領域を
        // 測る場合に前ページの位置で測らせないようにする（描画前なのでちらつかない）。
        if (push) resetScrollToTop();

        // 水和用データは差し替えたHTML自身が持つ #xte-initial-data
        // （初回SSRロードと同じ供給経路）。RESTからの再取得はしない（00文書 4.2節）。
        const data = readInitialDataFrom(page.doc);

        // 注意: pushState は必ず mount() の後に行う。routes/grid-entry.js は
        // 「`xte-grid` の connectedCallback が記録した window.location.href」と
        // 遷移先URLの一致でハイドレーション要否を判定しているため、先にURLを
        // 書き換えると「このURLの内容は反映済み」と誤判定され一覧が空になる。
        await mount(route.name, mod, data, url);
        currentUrlKey = url.href;

        // タイトルはサーバーHTMLの <title>（XTE_Seo::build_document_title() の出力
        // そのもの）が正。従来の titleHint 機構（リンク文言からの推定）は、
        // <title> が空という異常時のフォールバックへ降格した。
        const fetchedTitle = page.title.trim();
        const resolved = fetchedTitle !== '' ? fetchedTitle : resolveTitle(url, data, titleHint);
        if (resolved) {
          document.title = resolved;
          titleByUrl.set(url.href, resolved);
        }

        const state = {
          [HISTORY_MARKER]: true,
          route: route.name,
          url: url.href,
          title: document.title,
        };

        if (push) {
          if (url.href === window.location.href) {
            // 同じURLへの遷移では履歴を増やさない（戻るボタンが1回効かなくなるのを防ぐ）。
            history.replaceState(state, '', url.href);
          } else {
            history.pushState(state, '', url.href);
          }
          // 新規遷移は必ず先頭から表示する。mount() 中の処理（フォーカス移動等）が
          // スクロールを動かした可能性に備え、水和前のリセットに加えてここでも戻す。
          resetScrollToTop();
        } else {
          // popstate由来: URLはブラウザが既に変更済み。タイトルを含む最新のstateを
          // 同じエントリへ書き戻したうえで、記録済みのスクロール位置を復元する。
          // 復元は mount() 完了後のこの時点で行う（本文の高さが確定する前に
          // scrollTop を書くと、コンテンツ不足でクランプされ 0 に落ちるため）。
          history.replaceState(state, '', url.href);
          setScrollTop(scrollPositions.get(url.href) ?? 0);
        }

        // 遷移が「起きたこと」を、支援技術と他コンポーネントの双方へ伝える。
        focusMainContainer();
        announceNavigation(document.title);
        document.dispatchEvent(
          new CustomEvent(NAVIGATED_EVENT, {
            detail: { route: route.name, url, href: url.href, title: document.title, push },
          })
        );
      };

      // View Transitions API。非対応ブラウザ（Firefox等）ではそのまま同期描画する
      // （00文書 3.1節: JS無効・API非対応でも機能自体は壊れない設計）。
      if (typeof document.startViewTransition === 'function') {
        const transition = document.startViewTransition(() => applyRender());
        // finished / ready はアニメーションの進行を表すPromiseで、トランジションが
        // スキップされた場合（連続遷移で後発の startViewTransition が先行を中断した
        // 場合など）に AbortError でrejectする。どちらも未処理のrejectionにしない
        // ためだけに受ける（実測: ready を受けないと連続タブ切り替えで
        // 「Transition was skipped」の unhandledrejection が発生した）。
        transition.ready.catch(() => {});
        transition.finished.catch(() => {});
        // 描画コールバックの成否は updateCallbackDone で受け取る。
        // 旧実装は `await transition.finished.catch(() => {})` としていたため、
        // mount() が投げた例外まで握りつぶしていた。その結果、DOMだけが差し替わり、
        // URL・タイトル・ナビのアクティブ表示は更新されず、フォールバックも走らない
        // という無言の破損になっていた（ユーザー報告「ボタンが押せない」の実体）。
        await transition.updateCallbackDone;
      } else {
        await applyRender();
      }
    } catch (err) {
      if (seq !== navSeq) return; // 後発の遷移が進行中なら、こちらの失敗で画面を動かさない
      // 遷移に失敗したら通常のブラウザ遷移へフォールバックする。サーバーは常に完全な
      // HTMLを返せるため、これで必ず正しい画面に復帰する（00文書 原則2）。
      // エラーを握りつぶして白画面・本文据え置きのまま放置することは絶対にしない。
      console.error('[router] 遷移に失敗しました。通常遷移にフォールバックします。', err);
      onError?.(err, url);
      window.location.assign(url.href);
    }
  }

  function onClick(event) {
    const link = event.target instanceof Element ? event.target.closest(INTERNAL_LINK_SELECTOR) : null;
    if (!link) return;
    const url = shouldInterceptClick(event, link);
    if (!url) return;

    event.preventDefault();
    navigate(url, { push: true, titleHint: titleHintFromLink(link) }).catch((err) => {
      console.error('[router] navigate() 中に想定外のエラーが発生しました。', err);
      onError?.(err, url);
    });
  }

  function onPopState(event) {
    const state = event.state;

    if (state && state[HISTORY_MARKER]) {
      const url = new URL(state.url || window.location.href, window.location.origin);
      // エントリに保存しておいたサーバー由来のタイトルを、データ取得の完了を待たずに戻す。
      if (typeof state.title === 'string' && state.title !== '') {
        document.title = state.title;
      }
      navigate(url, { push: false }).catch((err) => {
        console.error('[router] popstate 遷移に失敗しました。', err);
        onError?.(err, url);
      });
      return;
    }

    if (state) {
      // 他コンポーネントが「同一ページ内の状態変化」として積んだエントリ
      // （xte-search-modal.js の `{xteSearchModal}`、xte-comments.js の `{xteCommentsOpen}`、
      //  xte-grid.js の `{xteGridFilter}`、routes/search-entry.js の `{xteRoute:'search'}` など）。
      // 所有者側がそれぞれ popstate を購読しているため、ルーターは介入しない。
      //
      // 旧実装は `state.xteRoute` の有無だけを見ていたため、`url` を持たない
      // routes/search-entry.js の `{ xteRoute: 'search' }` を自分のエントリと誤認し、
      // `new URL(undefined, origin)` すなわち `/undefined` へ実遷移させてしまっていた。
      return;
    }

    // クライアント遷移が始まる前のエントリ（stateを持たない）へ戻った場合は、
    // サーバーHTMLの正しさを担保するため素直にフルリロードで復帰する（00文書 原則2）。
    window.location.reload();
  }

  document.addEventListener('click', onClick, { signal: controller.signal });
  window.addEventListener('popstate', onPopState, { signal: controller.signal });

  // 初回ロード時点のURLをヒストリーの起点として記録する（pushStateはしない。
  // 現在表示中のHTMLはサーバー描画済みのため、ここで再描画は行わない）。
  // state にはサーバーが返した <title> をそのまま持たせ、他ページから戻ってきたときに
  // 元のタイトルへ正確に復帰できるようにする。
  const initialRoute = matchRoute(new URL(window.location.href));
  history.replaceState(
    {
      [HISTORY_MARKER]: true,
      route: initialRoute ? initialRoute.name : null,
      url: window.location.href,
      title: document.title,
    },
    '',
    window.location.href
  );

  return {
    navigate,
    dispose() {
      controller.abort();
      announcer?.remove();
      announcer = null;
    },
  };
}
